import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Fab,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SendIcon from "@mui/icons-material/Send";
import { MessageSquareText } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import { API_BASE_URL } from "../../../../config";
import { showSnackbar } from "../../../../redux/slices/snackbar";

const MAX_MESSAGE_LENGTH = 250;

export default function ChatComponent({ canSendMessages }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { idUid } = useParams();
  const scrollRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const loggedInUserId = sessionStorage.getItem("uid");

  const handleSnackbar = useCallback(
    (message, severity) => {
      dispatch(showSnackbar({ message, severity }));
    },
    [dispatch]
  );

  const scrollToBottom = useCallback((behavior = "smooth") => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior,
    });
  }, []);

  const fetchMessages = useCallback(async () => {
    const token = sessionStorage.getItem("token");
    if (!idUid || !token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/issue/list/${idUid}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();

      if (result.status === -1) {
        handleSnackbar(result.message, "error");
        sessionStorage.clear();
        navigate("/auth/sign-in");
        return;
      }
      if (result.status === 0) {
        handleSnackbar(result.message, "error");
        return;
      }
      setMessages(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      handleSnackbar(
        error?.message || "Impossibile recuperare i messaggi.",
        "error"
      );
    }
  }, [handleSnackbar, idUid, navigate]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    scrollToBottom("auto");
  }, [messages, scrollToBottom]);

  const groupedMessages = useMemo(() => {
    const groups = [];
    messages.forEach((message) => {
      const date = message.dataMessaggio?.split(" ")[0] || "Senza data";
      const lastGroup = groups[groups.length - 1];
      if (!lastGroup || lastGroup.date !== date) {
        groups.push({ date, messages: [message] });
      } else {
        lastGroup.messages.push(message);
      }
    });
    return groups;
  }, [messages]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    setShowScrollButton(scrollTop + clientHeight < scrollHeight - 16);
  };

  const handleSend = async () => {
    const message = input.trim();
    if (
      !message ||
      message.length > MAX_MESSAGE_LENGTH ||
      !canSendMessages ||
      isSending
    ) {
      return;
    }

    const token = sessionStorage.getItem("token");
    setIsSending(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/issue/${idUid}/messages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ data: { message } }),
        }
      );
      const result = await response.json();

      if (result.status === -1) {
        handleSnackbar(result.message, "error");
        sessionStorage.clear();
        navigate("/auth/sign-in");
        return;
      }
      if (result.status === 0) {
        handleSnackbar(result.message, "error");
        return;
      }

      setInput("");
      await fetchMessages();
    } catch (error) {
      handleSnackbar(
        error?.message || "Invio del messaggio non riuscito.",
        "error"
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: { xs: 360, md: 380 },
        borderTop: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.primary.main, 0.025),
      }}
    >
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 2, sm: 3 },
          py: 2.5,
          scrollbarWidth: "thin",
        }}
      >
        {groupedMessages.length === 0 ? (
          <Box
            sx={{
              minHeight: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              color: "text.secondary",
              px: 3,
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
                mb: 1.5,
              }}
            >
              <MessageSquareText size={24} />
            </Box>
            <Typography fontWeight={700} color="text.primary">
              Nessun aggiornamento
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Scrivi il primo messaggio per iniziare la lavorazione.
            </Typography>
          </Box>
        ) : (
          groupedMessages.map((group, groupIndex) => (
            <Box key={`${group.date}-${groupIndex}`}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 2 }}
              >
                <Box sx={{ height: 1, flex: 1, bgcolor: "divider" }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 650 }}
                >
                  {group.date}
                </Typography>
                <Box sx={{ height: 1, flex: 1, bgcolor: "divider" }} />
              </Box>

              {group.messages.map((message, messageIndex) => {
                const isSelf =
                  String(message.idUIDUserId) === String(loggedInUserId);
                const isSystem = !message.idUIDUserId;
                const senderName =
                  [message.nome, message.cognome].filter(Boolean).join(" ") ||
                  (isSystem ? "Mobility Express" : "Operatore");
                const time =
                  message.dataMessaggio?.split(" ")[1]?.substring(0, 5) || "";
                const avatarText =
                  message.nomeAvatar ||
                  senderName
                    .split(" ")
                    .slice(0, 2)
                    .map((part) => part[0])
                    .join("")
                    .toUpperCase();
                const accentColor = isSystem
                  ? theme.palette.warning.main
                  : isSelf
                  ? theme.palette.primary.main
                  : theme.palette.secondary.main;

                return (
                  <Box
                    key={message.id || `${groupIndex}-${messageIndex}`}
                    sx={{
                      display: "flex",
                      flexDirection: isSelf ? "row-reverse" : "row",
                      alignItems: "flex-end",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 30,
                        height: 30,
                        bgcolor: alpha(accentColor, 0.14),
                        color: accentColor,
                        fontSize: "0.7rem",
                        fontWeight: 800,
                      }}
                    >
                      {avatarText}
                    </Avatar>
                    <Box
                      sx={{
                        maxWidth: { xs: "82%", sm: "72%" },
                        px: 2,
                        py: 1.25,
                        borderRadius: isSelf
                          ? "16px 16px 4px 16px"
                          : "16px 16px 16px 4px",
                        bgcolor: isSelf
                          ? alpha(
                              theme.palette.primary.main,
                              theme.palette.mode === "dark" ? 0.26 : 0.11
                            )
                          : "background.paper",
                        border: `1px solid ${
                          isSelf
                            ? alpha(theme.palette.primary.main, 0.2)
                            : theme.palette.divider
                        }`,
                        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
                      }}
                    >
                      {!isSelf ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: accentColor,
                            fontWeight: 750,
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          {senderName}
                        </Typography>
                      ) : null}
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}
                      >
                        {message.messaggio}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", textAlign: "right", mt: 0.5 }}
                      >
                        {time}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))
        )}
      </Box>

      {showScrollButton ? (
        <Fab
          size="small"
          onClick={() => scrollToBottom()}
          aria-label="Vai agli ultimi messaggi"
          sx={{
            position: "absolute",
            bottom: 96,
            right: 24,
            bgcolor: "background.paper",
            color: "text.secondary",
          }}
        >
          <ArrowDownwardIcon fontSize="small" />
        </Fab>
      ) : null}

      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          alignItems: "flex-start",
          p: { xs: 2, sm: 2.5 },
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder={
            canSendMessages
              ? "Scrivi un aggiornamento…"
              : "Non disponi dei permessi per inviare messaggi"
          }
          value={input}
          disabled={!canSendMessages || isSending}
          onChange={(event) => {
            if (event.target.value.length <= MAX_MESSAGE_LENGTH) {
              setInput(event.target.value);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleSend();
            }
          }}
          helperText={`${input.length}/${MAX_MESSAGE_LENGTH}`}
          FormHelperTextProps={{
            sx: { textAlign: "right", mx: 0, mt: 0.75, fontSize: "0.68rem" },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: alpha(theme.palette.background.default, 0.7),
            },
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!input.trim() || !canSendMessages || isSending}
          aria-label="Invia messaggio"
          sx={{
            width: 40,
            height: 40,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            "&:hover": { bgcolor: "primary.dark" },
            "&.Mui-disabled": {
              bgcolor: alpha(theme.palette.text.primary, 0.08),
              color: "text.disabled",
            },
          }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}
